import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { NotificationType, PartialToast, ShAbstractToastService, ShToastFactory, Toast } from '@stylehaven/components';
import { ToastState } from '../state/state';
import { AddToast, DeleteToast, UpdateToast, UpsertToast } from '../state/toast.actions';
import { selectAll } from '../state/toast.reducer';

@Injectable()
export class ToastHelper implements ShAbstractToastService {
    private readonly TYPES = ['started', 'completed', 'failed', 'START', 'SUCCESS', 'FAIL'];

    toasts$: Observable<Toast[]>;

    constructor(
        private store: Store<{ sharedToast: ToastState }>,
        private toastFactory: ShToastFactory,
        private readonly translateService: TranslateService
    ) {
        this.toasts$ = this.buildToastStream();
    }

    // C(R)UD
    addToast(toast: Toast): void {
        this.store.dispatch(UpsertToast({ toast }));
    }

    updateToast(toastId: number, toast: PartialToast): void {
        this.store.dispatch(UpdateToast({ id: toastId, toast }));
    }

    closeToast(toastId: number): void {
        this.store.dispatch(DeleteToast({ id: toastId }));
    }

    // #region Standalone Toasts
    addWarning(messagePath: string): Toast {
        const toast: Toast = this.toastFactory.create(
            this.getGenericWarningToast(this.translateService.instant(messagePath))
        );
        this.add(toast);
        return toast;
    }

    createStartToast(action: string, items?: number): Toast {
        const toast: Toast = this.toastFactory.create(this.getToastByType(action, 'START', items));
        this.add(toast);
        return toast;
    }

    createSuccessToast(action: string, items?: number): Toast {
        const toast: Toast = this.toastFactory.create(this.getToastByType(action, 'SUCCESS', items));
        this.add(toast);
        return toast;
    }

    createFailToast(action: string, items?: number): Toast {
        const toast: Toast = this.toastFactory.create(this.getToastByType(action, 'FAIL', items));
        this.add(toast);
        return toast;
    }
    // #endregion

    // #region Update Partial Toasts
    updateToSuccess(toastId: number, action: string, items?: number, link?: string, linkText?: string): PartialToast {
        const toast: PartialToast = this.getToastByType(action, 'SUCCESS', items, link, linkText);
        this.update(toastId, toast);
        return toast;
    }

    updateToFailure(toastId: number, action: string, items?: number): PartialToast {
        const toast: PartialToast = this.getToastByType(action, 'FAIL', items);
        this.update(toastId, toast);
        return toast;
    }
    // #endregion

    remove(id: number): void {
        this.delete(id);
    }

    private getToastByType(
        action: string,
        type: 'START' | 'SUCCESS' | 'FAIL',
        items?: number,
        link?: string,
        linkText?: string
    ): PartialToast {
        switch (type) {
            case 'START':
                return this.getGenericToast(
                    this.translateService.instant(this.getTranslationKey(action, type), { items })
                );
            case 'SUCCESS':
                return this.getGenericSuccessToast(
                    this.translateService.instant(this.getTranslationKey(action, type), { items }),
                    link,
                    linkText
                );
            case 'FAIL':
                return this.getGenericFailureToast(
                    this.translateService.instant(this.getTranslationKey(action, type), { items })
                );
        }
    }

    private getTranslationKey(action: string, type: string): string {
        const parts = [];

        if (!action.includes('toast')) {
            parts.push('toasts');
        }
        parts.push(action);
        if (!this.TYPES.some(value => action.includes(value))) {
            parts.push(type);
        }
        return parts.join('.');
    }

    private add(toast: Toast): void {
        this.store.dispatch(AddToast({ toast }));
    }

    private upsert(toast: Toast): void {
        this.store.dispatch(UpsertToast({ toast }));
    }

    private update(id: number, toast: PartialToast): void {
        this.store.dispatch(UpdateToast({ id, toast }));
    }

    private delete(id: number): void {
        this.store.dispatch(DeleteToast({ id }));
    }

    private buildToastStream(): Observable<Toast[]> {
        return this.store.select(state => {
            return selectAll(state.sharedToast);
        });
    }

    private getGenericToast(message: string, link?: string, linkText?: string): PartialToast {
        return {
            message,
            type: NotificationType.Update,
            icon: 'spinner',
            iconRotate: true,
            autoClose: false,
            closable: true,
            ...(link && { navigationLink: { route: link } }),
            ...(linkText && { linkText })
        };
    }

    private getGenericWarningToast(message: string): PartialToast {
        return {
            message,
            type: NotificationType.Warning,
            icon: 'warning'
        };
    }

    private getGenericSuccessToast(message: string, link?: string, linkText?: string): PartialToast {
        return {
            message,
            type: NotificationType.Success,
            icon: 'thumbs-up',
            iconRotate: false,
            autoClose: true,
            autoCloseDelay: 10000,
            closable: true,
            ...(link && { navigationLink: { route: link } }),
            ...(linkText && { linkText })
        };
    }

    private getGenericFailureToast(message: string, link?: string, linkText?: string): PartialToast {
        return {
            message,
            type: NotificationType.Error,
            icon: 'delete',
            iconRotate: false,
            autoClose: true,
            autoCloseDelay: 10000,
            closable: true,
            ...(link && { navigationLink: { route: link } }),
            ...(linkText && { linkText })
        };
    }
}
